import { writeBatch } from '../../libs/firestore-wrapper';
import { getFoodDetails, getUserMenuCategoryDetails } from '../../modals/order';
import { deleteCategoryFromUserMenuList, moveFoodsBetweenCategoriesInUserMenuList } from '../UserMenuContext/userMenu.services';
import { IUserMenuCategory } from '../../pages/CategoriesManagement/categoriesManagement.types';
import { IFoodDetails } from '../../pages/MenuManagement/menu.types';

type WriteBatch = ReturnType<typeof writeBatch>;

const updateCategoryDocumentsForFoodMove = (
  batch: WriteBatch,
  categories: IUserMenuCategory[],
  selectedCategoryIds: string[],
  selectedFoodIds: string[],
  initialCommonCategoryIds: string[],
  manuallyRemovedCategoryIds: string[] = []
) => {
  const selectedCategorySet = new Set(selectedCategoryIds);
  const initialCommonSet = new Set(initialCommonCategoryIds);
  const manuallyRemovedSet = new Set(manuallyRemovedCategoryIds);

  const categoriesToProcess = new Set<string>(selectedCategoryIds);
  initialCommonCategoryIds.forEach((catId) => {
    if (!selectedCategoryIds.includes(catId)) {
      categoriesToProcess.add(catId);
    }
  });
  manuallyRemovedCategoryIds.forEach((catId) => categoriesToProcess.add(catId));

  categories.forEach((category) => {
    if (!categoriesToProcess.has(category.id)) return;

    const existingFoodIds = category.foodIds ?? [];
    let updatedFoodIds = [...existingFoodIds];
    const shouldHaveFoods = selectedCategorySet.has(category.id);

    if (shouldHaveFoods) {
      selectedFoodIds.forEach((foodId) => {
        if (!updatedFoodIds.includes(foodId)) {
          updatedFoodIds.push(foodId);
        }
      });
    } else if (initialCommonSet.has(category.id) || manuallyRemovedSet.has(category.id)) {
      updatedFoodIds = updatedFoodIds.filter((foodId) => !selectedFoodIds.includes(foodId));
    }

    if (JSON.stringify(existingFoodIds.sort()) !== JSON.stringify(updatedFoodIds.sort())) {
      const ref = getUserMenuCategoryDetails(category.id);
      batch.update(ref, { foodIds: updatedFoodIds });
    }
  });
};

const updateFoodDocumentsForMove = (
  batch: WriteBatch,
  selectedFoodIds: string[],
  selectedCategoryIds: string[],
  foods: IFoodDetails[],
  initialCommonCategoryIds: string[],
  manuallyRemovedCategoryIds: string[] = []
) => {
  const initialCommonSet = new Set(initialCommonCategoryIds);

  selectedFoodIds.forEach((foodId) => {
    const food = foods.find((f) => f.id === foodId);
    const currentCategories = (food?.categories_menu_ids as string[]) ?? [];

    const updatedCategoriesSet = new Set(currentCategories);

    initialCommonCategoryIds.forEach((commonCat) => {
      if (!selectedCategoryIds.includes(commonCat)) {
        updatedCategoriesSet.delete(commonCat);
      }
    });

    manuallyRemovedCategoryIds.forEach((rm) => updatedCategoriesSet.delete(rm));

    selectedCategoryIds.forEach((catId) => updatedCategoriesSet.add(catId));

    const updatedCategories = Array.from(updatedCategoriesSet);

    const foodRef = getFoodDetails(foodId);
    batch.update(foodRef, { categories_menu_ids: updatedCategories });
  });
};

const removeCategoryFromFoods = (batch: WriteBatch, foods: IFoodDetails[], affectedFoodIds: string[], categoryIdToRemove: string) => {
  affectedFoodIds.forEach((foodId) => {
    const food = foods.find((f) => f.id === foodId);
    if (food && food.categories_menu_ids) {
      const currentCategories = food.categories_menu_ids as string[];
      const updatedCategories = currentCategories.filter((catId) => catId !== categoryIdToRemove);
      const foodRef = getFoodDetails(foodId);
      batch.update(foodRef, { categories_menu_ids: updatedCategories });
    }
  });
};

const syncUserMenuAfterMove = async (
  stadiumId: string,
  selectedFoodIds: string[],
  selectedCategoryIds: string[],
  initialCommonCategoryIds: string[],
  manuallyRemovedCategoryIds: string[] = []
) => {
  const allFromCategories = new Set<string>();
  const allToCategories = new Set(selectedCategoryIds);

  initialCommonCategoryIds.forEach((catId) => {
    if (!selectedCategoryIds.includes(catId)) {
      allFromCategories.add(catId);
    }
  });

  manuallyRemovedCategoryIds.forEach((catId) => allFromCategories.add(catId));

  if (allFromCategories.size > 0 || allToCategories.size > 0) {
    await moveFoodsBetweenCategoriesInUserMenuList(stadiumId, selectedFoodIds, Array.from(allFromCategories), Array.from(allToCategories));
  }
};

export const moveFoodsToCategories = async (
  stadiumId: string,
  selectedFoodIds: string[],
  selectedCategoryIds: string[],
  categories: IUserMenuCategory[],
  foods: IFoodDetails[],
  initialCommonCategoryIds: string[],
  manuallyRemovedCategoryIds: string[] = []
): Promise<void> => {
  if (selectedFoodIds.length === 0) {
    throw new Error('No foods selected');
  }

  const batch = writeBatch();
  updateCategoryDocumentsForFoodMove(batch, categories, selectedCategoryIds, selectedFoodIds, initialCommonCategoryIds, manuallyRemovedCategoryIds);
  updateFoodDocumentsForMove(batch, selectedFoodIds, selectedCategoryIds, foods, initialCommonCategoryIds, manuallyRemovedCategoryIds);
  await batch.commit();
  await syncUserMenuAfterMove(stadiumId, selectedFoodIds, selectedCategoryIds, initialCommonCategoryIds, manuallyRemovedCategoryIds);
};

export const deleteCategory = async (stadiumId: string, categoryId: string, category: IUserMenuCategory, foods: IFoodDetails[]): Promise<void> => {
  const batch = writeBatch();

  const categoryRef = getUserMenuCategoryDetails(categoryId);
  batch.delete(categoryRef);

  const affectedFoodIds = category?.foodIds || [];
  removeCategoryFromFoods(batch, foods, affectedFoodIds, categoryId);

  await batch.commit();

  await deleteCategoryFromUserMenuList(stadiumId, categoryId);
};
